<template>
    <AppLayout>
        <div class="bg-white shadow overflow-hidden sm:rounded mb-5">
            <div class="px-4 py-5 sm:px-6">
                <h2 class="text-xl leading-6 font-medium text-gray-900">
                    {{ $t("tenants.edit.title", { name: tenant.name }) }}
                </h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">{{ $t("tenants.edit.description") }}</p>
            </div>
            <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl class="sm:divide-y sm:divide-gray-200">
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("tenants.forms.id.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ tenant.id }}</dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("tenants.forms.url.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <Link
                                :href="`${env.url}/${tenant.id}`"
                                class="font-medium text-lightblue-700 hover:text-lightblue-500"
                            >
                                {{ `${env.url}/${tenant.id}` }}
                            </Link>
                        </dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">
                            {{ $t("airtable.forms.airtable_record.title") }}
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <a
                                :href="`https://airtable.com/tbluw0XnSaCR0JA1Z/viwKaFmE2tSl29lb3/${tenant.airtable_record_id}`"
                                target="_blank"
                                class="font-medium text-lightblue-700 hover:text-lightblue-500"
                            >
                                {{ tenant.airtable_record_id }}
                            </a>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>

        <div class="overflow-hidden sm:rounded">
            <div class="pt-5 pb-2">
                <h2 class="text-lg font-medium text-gray-900">{{ $t("tenants.forms.projects.title") }}</h2>
            </div>

            <div v-if="!hasProjects" class="max-w-7xl mx-auto">
                <div class="w-full m-auto justify-center items-center">
                    <div class="flex">
                        <fa-icon icon="folder-open" class="mx-auto text-7xl text-gray-300"></fa-icon>
                    </div>
                    <h2 class="my-2 text-4xl text-gray-300 text-center">
                        {{ $t("tenants.forms.projects.no_projects_yet") }}
                    </h2>
                </div>
            </div>

            <div v-if="hasProjects" class="py-5 sm:p-0">
                <Table :headers="['name', 'type_env', 'status', 'actions']" :content="currentProjects" />
            </div>
        </div>

        <div class="overflow-hidden sm:rounded">
            <div class="flex pt-5 pb-2">
                <h2 class="text-lg font-medium text-gray-900">
                    {{ $t("tenants.forms.users.title") }}
                </h2>
                <Link
                    class="text-sm cursor-pointer ml-auto mt-auto text-lightblue-700 hover:text-lightblue-500"
                    :href="route('universal.users.create', { tenant: tenant.id })"
                    >{{ $t("tenants.forms.users.create") }}
                </Link>
            </div>

            <div v-if="!hasUsers" class="max-w-7xl mx-auto">
                <div class="w-full m-auto justify-center items-center">
                    <div class="flex">
                        <fa-icon icon="users" class="mx-auto text-7xl text-gray-300"></fa-icon>
                    </div>
                    <h2 class="my-2 text-4xl text-gray-300 text-center">
                        {{ $t("tenants.forms.users.no_users_yet") }}
                    </h2>
                </div>
            </div>

            <div v-if="hasUsers" class="py-5 sm:p-0">
                <Table
                    :headers="['id', 'name', 'email', 'active', 'actions']"
                    :content="currentUsers"
                    :loading-items="currentlyProcessing"
                    @action-click="actionClickHandler"
                />
            </div>
        </div>
    </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import FaIcon from "@/Components/FaIcon";
import Table from "@/Components/Table";
import { computed, ref } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { Link } from "@inertiajs/inertia-vue3";
import env from "../../env";

export default {
    name: "TenantUpdate",
    components: { FaIcon, AppLayout, Table, Link },
    props: {
        tenant: Object,
        projects: Object,
        users: Object,
    },
    setup(props) {
        const hasProjects = computed(() => Boolean(props.projects.data.length));
        const currentProjects = computed(() => (props.projects.data ? props.projects.data : []));

        const hasUsers = computed(() => Boolean(props.users.data.length));
        const currentlyProcessing = ref([]);
        const currentUsers = computed(() => {
            if (!props.users.data) {
                return [];
            }

            return props.users.data;
        });

        const actionClickHandler = (data) => {
            switch (data.action) {
                case "password-reset":
                    Inertia.post(
                        route(`universal.users.resetpassword`, {
                            tenant: props.tenant.id,
                            user: currentUsers.value[data.index].id,
                        })
                    ).finally(() => {
                        const index = currentlyProcessing.value.findIndex(
                            (item) => item.index === data.index && item.action === data.action
                        );

                        if (index !== -1) {
                            currentlyProcessing.value.splice(index, 1);
                        }
                    });

                    break;
                case "activate":
                case "deactivate":
                    Inertia.delete(
                        route(`universal.users.destroy`, {
                            tenant: props.tenant.id,
                            user: currentUsers.value[data.index].id,
                        })
                    ).finally(() => {
                        const index = currentlyProcessing.value.findIndex(
                            (item) => item.index === data.index && item.action === data.action
                        );

                        if (index !== -1) {
                            currentlyProcessing.value.splice(index, 1);
                        }
                    });

                    break;
                default:
                    return;
            }

            currentlyProcessing.value.push({
                index: data.index,
                action: data.action,
            });
        };
        const createUserClickHandler = () => {};

        return {
            hasProjects,
            currentProjects,
            hasUsers,
            currentUsers,
            currentlyProcessing,
            actionClickHandler,
            createUserClickHandler,
            env,
        };
    },
};
</script>
